import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// scrollY for each history entry, keyed by location.key.
const positions = new Map();

const INTERRUPTS = ["wheel", "touchstart", "keydown"];

/**
 * Opening a page jumps to the top instantly; going back or forward returns you
 * to where you were. Nothing animates — the previous version smooth-scrolled on
 * every navigation, so you watched the page glide upward on each click and lost
 * your place in the listing on the way back.
 */
function ScrollManager() {
    const { key, pathname } = useLocation();
    const navigationType = useNavigationType(); // "POP" on back/forward
    const previousPath = useRef(pathname);

    // The browser keeps its own scroll memory per history entry and reapplies it
    // asynchronously on back/forward, landing after the restore below and
    // overriding it. Take manual control so there is a single source of truth.
    useEffect(() => {
        if (!("scrollRestoration" in window.history)) return undefined;
        const previous = window.history.scrollRestoration;
        window.history.scrollRestoration = "manual";
        return () => {
            window.history.scrollRestoration = previous;
        };
    }, []);

    // Keep the offset for the entry currently on screen up to date.
    //
    // This must be a layout effect, not a passive one. Passive cleanup runs
    // *after* React swaps in the next route; the incoming page is shorter, the
    // browser clamps scrollY down to fit it, and the listener then records that
    // clamped value as "where you were" (818 -> 111 in testing). Layout cleanup
    // runs during the commit, before the swap, so it unbinds and captures the
    // real offset.
    useLayoutEffect(() => {
        const save = () => positions.set(key, window.scrollY);
        window.addEventListener("scroll", save, { passive: true });
        return () => {
            window.removeEventListener("scroll", save);
            save(); // final offset, recorded while the old page is still mounted
        };
    }, [key]);

    useEffect(() => {
        const saved = navigationType === "POP" ? positions.get(key) : undefined;
        const pathChanged = previousPath.current !== pathname;
        previousPath.current = pathname;

        if (saved === undefined) {
            // Only reset for a real page change. In-page anchors (the
            // "#collection" button) keep the same pathname but get their own
            // location key, so keying this on `key` would yank the page to the
            // top instead of letting the anchor work — a regression the old
            // pathname-keyed version did not have.
            //
            // behavior:"instant" is required, not cosmetic: html has
            // scroll-behavior:smooth (kept for that anchor), which would
            // otherwise animate this and reintroduce the exact problem.
            if (pathChanged) {
                window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }
            return undefined;
        }

        // Products are fetched on mount, so the restored page starts near-empty
        // and too short to scroll to `saved`. Retry on a timer until it fits.
        // (Deliberately not a ResizeObserver on the body: scrolling reflows the
        // page, which re-fires the observer, which scrolls again — that loop
        // pegs the renderer.)
        //
        // Scroll anchoring has to be off for the duration. As the list renders,
        // the browser inserts content above the viewport and silently shifts
        // scrollTop to keep the anchored element still — which dragged a
        // restored 818 to 2046 in testing, without emitting a scroll event.
        const root = document.documentElement;
        const previousAnchor = root.style.overflowAnchor;
        root.style.overflowAnchor = "none";

        let settled = false;
        let ticker;
        let deadline;

        const finish = () => {
            if (settled) return;
            settled = true;
            clearInterval(ticker);
            clearTimeout(deadline);
            root.style.overflowAnchor = previousAnchor;
            INTERRUPTS.forEach((evt) => window.removeEventListener(evt, finish));
        };

        const apply = () => {
            if (settled) return;
            // Stop nudging once we are there, but leave anchoring off until the
            // deadline so late-arriving images cannot shunt the page again.
            if (Math.abs(window.scrollY - saved) > 2) {
                window.scrollTo({ top: saved, left: 0, behavior: "instant" });
            }
        };

        // Never fight the user: if they scroll while we are waiting, stand down.
        INTERRUPTS.forEach((evt) => window.addEventListener(evt, finish, { passive: true }));
        ticker = setInterval(apply, 60);
        deadline = setTimeout(finish, 2500);
        apply();

        return finish;
    }, [key, pathname, navigationType]);

    return null;
}

export default ScrollManager;
